import { RefObject, useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useTranslationI18 } from "../../../../services/hooks/useTranslationI18";
import { SearchIcon } from "../../S-Map/Map.icons";
import { SearchCloseIcon } from "./MenuSearchBar.icons";
import styles from "./MenuSearchBar.module.scss";

const SEARCH_GLYPH_SIZE = 50 / 3;

export const MenuSearchBar = (props: {
  expanded: boolean;
  value: string;
  onChange: (value: string) => void;
  onExpand: () => void;
  onCollapse: () => void;
  /**
   * Focused by the caller once the row has settled where it pins, rather than
   * on expand: see the focus proxy below.
   */
  inputRef: RefObject<HTMLInputElement>;
}) => {
  const { expanded, value, onChange, onExpand, onCollapse, inputRef } = props;
  const { t } = useTranslationI18();
  const proxyRef = useRef<HTMLInputElement>(null);
  const [mounted, setMounted] = useState(false);

  useEffect(() => setMounted(true), []);

  /*
   * iOS only raises the keyboard for a focus made inside the tap itself, and
   * when it rises over the focused field it pans the whole viewport to keep
   * that field in sight -- carrying the fixed header off screen. The real
   * input does not exist at tap time and would sit mid-page if it did, so the
   * tap focuses this stand-in, fixed at the top of the screen where the
   * keyboard never covers it. onExpand runs first, so the caller can start
   * the page on its way to the pin within the same tap. The caller hands focus
   * to the real input once the row has settled; the keyboard stays up across
   * the move. The stand-in mirrors the query so nothing typed in between is
   * lost.
   */
  const expand = () => {
    onExpand();
    proxyRef.current?.focus({ preventScroll: true });
  };

  return (
    <div
      className={`${styles.search} ${expanded ? styles.expanded : ""}`}
      data-testid={expanded ? "menu-search-bar" : "menu-search-collapsed"}
      data-search-pill=""
    >
      <button
        type="button"
        className={styles.search_toggle}
        onClick={expanded ? undefined : expand}
        aria-label={t("common:search")}
        aria-expanded={expanded}
        tabIndex={expanded ? -1 : 0}
        data-testid="menu-search-toggle"
      >
        <SearchIcon size={SEARCH_GLYPH_SIZE} />
      </button>

      {expanded && (
        <>
          <input
            ref={inputRef}
            type="text"
            className={styles.search_input}
            placeholder={t("common:search")}
            value={value}
            onChange={(event) => onChange(event.target.value)}
            data-testid="menu-search-input"
          />
          <div className={styles.search_actions}>
            {!!value && (
              <button
                type="button"
                className={styles.search_clear}
                onClick={() => onChange("")}
                data-testid="menu-search-clear"
              >
                {t("common:clear")}
              </button>
            )}
            <button
              type="button"
              className={styles.search_close}
              onClick={onCollapse}
              aria-label={t("common:cancel")}
              data-testid="menu-search-close"
            >
              <SearchCloseIcon />
            </button>
          </div>
        </>
      )}

      {mounted &&
        createPortal(
          <input
            ref={proxyRef}
            type="text"
            className={styles.focus_proxy}
            value={value}
            onChange={(event) => onChange(event.target.value)}
            tabIndex={-1}
            aria-hidden="true"
            data-testid="menu-search-focus-proxy"
          />,
          document.body
        )}
    </div>
  );
};
